// PATH: src/components/documents/objectivesView.ts
/**
 * Objectifs d'un chapitre : mise en forme pure (testée), sans React.
 */
import type { DocumentObjective, ObjectivesResponse } from "../../api/objectives/objectives";

/** Compacte une suite d'ordinaux en plages lisibles : "12-15, 19". */
export const formatOrdinalRanges = (ordinals: number[]): string => {
  if (ordinals.length === 0) return "—";
  const sorted = [...ordinals].sort((a, b) => a - b);
  const parts: string[] = [];
  let begin = sorted[0];
  let prev = sorted[0];
  const flush = () => parts.push(begin === prev ? `${begin}` : `${begin}-${prev}`);
  for (const o of sorted.slice(1)) {
    if (o === prev + 1) {
      prev = o;
      continue;
    }
    flush();
    begin = o;
    prev = o;
  }
  flush();
  return parts.join(", ");
};

/**
 * Numéros de passages pour un lecteur : l'ordinal interne commence à 0, la
 * notion affichée à 1. Plages compactées, la dernière jointe par « et » :
 * [1, 4] → « 2 et 5 », [1, 2, 3, 7] → « 2-4 et 8 ».
 */
export const formatNotionList = (ordinals: number[]): string => {
  if (ordinals.length === 0) return "—";
  const parts = formatOrdinalRanges(ordinals.map((o) => o + 1)).split(", ");
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} et ${parts[parts.length - 1]}` : parts[0];
};

/**
 * Couverture du chapitre par les objectifs.
 *
 * Les trous sont l'information qui compte : un passage du cours que plus
 * aucun objectif ne référence n'est enseigné à personne.
 */
export const computeCoverage = (objectives: DocumentObjective[]) => {
  const covered = new Set<number>();
  objectives.forEach((o) => (o.source_chunk_ordinals ?? []).forEach((n) => covered.add(n)));
  if (covered.size === 0) return { covered: 0, span: 0, gaps: [] as number[] };

  const all = [...covered].sort((a, b) => a - b);
  const min = all[0];
  const max = all[all.length - 1];
  const gaps: number[] = [];
  for (let i = min; i <= max; i += 1) {
    if (!covered.has(i)) gaps.push(i);
  }
  return { covered: covered.size, span: max - min + 1, gaps };
};

/** Ordre de tri par nature : stable, indépendant de l'ordre d'arrivée. */
export const compareByKind = (a: DocumentObjective, b: DocumentObjective) => {
  const byKind = (a.kind ?? "").localeCompare(b.kind ?? "");
  return byKind !== 0 ? byKind : a.ordinal - b.ordinal;
};

/**
 * Bilan de l'upsert par provenance, en français et en clair.
 *
 * `orphaned` et `reused` se lisent ENSEMBLE : un objectif réutilisé garde son
 * UUID (la progression élève survit), un orphelin emporte en cascade les
 * corrections éditoriales et les signalements portés sur ses faits. C'est sur
 * ces chiffres que se prend la décision de rejouer le parc — ils ne peuvent
 * pas rester dans les logs du serveur.
 */
export const formatReplacement = (
  res: ObjectivesResponse,
): { text: string; severity: "success" | "warning" } | null => {
  const { orphaned, reused, orphaned_overrides, orphaned_reports } = res;
  if (orphaned == null && reused == null && orphaned_overrides == null && orphaned_reports == null) {
    // Serveur qui ne publie pas encore ces compteurs : rien à annoncer.
    return null;
  }

  const parts: string[] = [`${res.objectives.length} objectif(s) après extraction`];
  if (reused != null) parts.push(`${reused} réutilisé(s), progression conservée`);
  if (orphaned != null) parts.push(`${orphaned} orphelin(s), progression perdue`);
  const lost = (orphaned_overrides ?? 0) + (orphaned_reports ?? 0);
  if (lost > 0) {
    parts.push(
      `${orphaned_overrides ?? 0} correction(s) éditoriale(s) et ` +
        `${orphaned_reports ?? 0} signalement(s) emportés par la cascade`,
    );
  }
  return {
    text: parts.join(" · ") + ".",
    severity: (orphaned ?? 0) > 0 || lost > 0 ? "warning" : "success",
  };
};
